import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { vehiclesApi } from '../../api/vehicles';
import { driversApi } from '../../api/drivers';
import toast from 'react-hot-toast';

const vehicleSchema = z.object({
  driver_id: z.union([z.string(), z.number(), z.null(), z.undefined()])
    .transform((val) => {
      if (val === null || val === undefined || val === '' || String(val).trim() === '') {
        return null;
      }
      return String(val);
    })
    .pipe(z.string().nullable().optional()),
  marca: z.string().min(1, 'La marca es requerida'),
  modelo: z.string().min(1, 'El modelo es requerido'),
  ano: z.number().min(1900, 'El año debe ser mayor a 1900').max(new Date().getFullYear() + 1, 'El año no puede ser futuro'),
  placas: z.string().min(1, 'Las placas son requeridas'),
  economico: z.string().min(1, 'El económico es requerido'),
  capacidad_pasajeros: z.number().min(1, 'La capacidad de pasajeros debe ser al menos 1'),
  equipaje: z.boolean(),
  tipo_vehiculo: z.string().optional(),
  color: z.string().optional(),
  ultima_fecha_mantenimiento: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Formato de fecha inválido (YYYY-MM-DD)').optional().or(z.literal('')),
  proximo_servicio: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Formato de fecha inválido (YYYY-MM-DD)').optional().or(z.literal('')),
  seguro_vigente: z.boolean(),
  activo: z.boolean(),
});

const VehicleForm = ({ vehicle, driverId, onClose, onSuccess }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const queryClient = useQueryClient();
  
  // Fetch drivers for the dropdown
  const { data: driversData } = useQuery({
    queryKey: ['drivers'],
    queryFn: () => driversApi.getAll({ page: 1, pageSize: 1000 }),
  });

  const drivers = Array.isArray(driversData) ? driversData : (driversData?.data || []);
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: zodResolver(vehicleSchema),
    defaultValues: {
      driver_id: driverId ? String(driverId) : (vehicle?.driver_id ? String(vehicle.driver_id) : ''),
      marca: vehicle?.marca || '',
      modelo: vehicle?.modelo || '',
      ano: vehicle?.ano || new Date().getFullYear(),
      placas: vehicle?.placas || '',
      economico: vehicle?.economico || '',
      capacidad_pasajeros: vehicle?.capacidad_pasajeros || 4,
      equipaje: vehicle?.equipaje === 1 || vehicle?.equipaje === true || false,
      tipo_vehiculo: vehicle?.tipo_vehiculo || '',
      color: vehicle?.color || '',
      ultima_fecha_mantenimiento: vehicle?.ultima_fecha_mantenimiento || '',
      proximo_servicio: vehicle?.proximo_servicio || '',
      seguro_vigente: vehicle?.seguro_vigente === 1 || vehicle?.seguro_vigente === true || false,
      activo: vehicle !== undefined && vehicle !== null 
        ? (vehicle.activo === 1 || vehicle.activo === true) 
        : true,
    },
  });

  const createMutation = useMutation({
    mutationFn: vehiclesApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
      toast.success('Vehículo creado exitosamente');
      onSuccess?.();
    },
    onError: (error) => {
      toast.error(error.message || 'Error al crear vehículo');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => vehiclesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
      toast.success('Vehículo actualizado exitosamente');
      onSuccess?.();
    },
    onError: (error) => {
      toast.error(error.message || 'Error al actualizar vehículo');
    },
  });

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      // Prepare data according to API structure
      const submitData = {
        driver_id: data.driver_id && data.driver_id.trim() !== '' ? String(data.driver_id) : null,
        marca: data.marca,
        modelo: data.modelo,
        ano: data.ano,
        placas: data.placas,
        economico: data.economico,
        capacidad_pasajeros: data.capacidad_pasajeros,
        equipaje: data.equipaje,
        tipo_vehiculo: data.tipo_vehiculo || '',
        color: data.color || '',
        ultima_fecha_mantenimiento: data.ultima_fecha_mantenimiento && data.ultima_fecha_mantenimiento.trim() !== '' 
          ? data.ultima_fecha_mantenimiento 
          : null,
        proximo_servicio: data.proximo_servicio && data.proximo_servicio.trim() !== '' 
          ? data.proximo_servicio 
          : null,
        seguro_vigente: data.seguro_vigente,
        activo: data.activo,
      };
      
      if (vehicle) {
        await updateMutation.mutateAsync({ id: vehicle.id, data: submitData });
      } else {
        await createMutation.mutateAsync(submitData);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{vehicle ? 'Editar Vehículo' : 'Nuevo Vehículo'}</h2>
          <button className="modal-close" onClick={onClose}>
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="modal-body">
          <div className="form-grid">
            <div className="form-group" style={{ display: driverId ? 'none' : 'flex' }}>
              <label htmlFor="driver_id">Chofer</label>
              <select
                id="driver_id"
                {...register('driver_id')}
                className={errors.driver_id ? 'error' : ''}
              >
                <option value="">Selecciona un chofer (opcional)</option>
                {drivers.map((driver) => (
                  <option key={driver.id} value={driver.id}>
                    {driver.nombre} {driver.apellido_paterno} {driver.apellido_materno}
                  </option>
                ))}
              </select>
              {errors.driver_id && (
                <span className="error-message">{errors.driver_id.message}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="marca">Marca *</label>
              <input
                id="marca"
                type="text"
                {...register('marca')}
                className={errors.marca ? 'error' : ''}
              />
              {errors.marca && (
                <span className="error-message">{errors.marca.message}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="modelo">Modelo *</label>
              <input
                id="modelo"
                type="text"
                {...register('modelo')}
                className={errors.modelo ? 'error' : ''}
              />
              {errors.modelo && (
                <span className="error-message">{errors.modelo.message}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="ano">Año *</label>
              <input
                id="ano"
                type="number"
                {...register('ano', { valueAsNumber: true })}
                className={errors.ano ? 'error' : ''}
                min="1900"
                max={new Date().getFullYear() + 1}
              />
              {errors.ano && (
                <span className="error-message">{errors.ano.message}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="placas">Placas *</label>
              <input
                id="placas"
                type="text"
                {...register('placas')}
                className={errors.placas ? 'error' : ''}
                style={{ fontFamily: 'monospace', textTransform: 'uppercase' }}
              />
              {errors.placas && (
                <span className="error-message">{errors.placas.message}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="economico">Económico *</label>
              <input
                id="economico"
                type="text"
                {...register('economico')}
                className={errors.economico ? 'error' : ''}
              />
              {errors.economico && (
                <span className="error-message">{errors.economico.message}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="capacidad_pasajeros">Capacidad de Pasajeros *</label>
              <input
                id="capacidad_pasajeros"
                type="number"
                {...register('capacidad_pasajeros', { valueAsNumber: true })}
                className={errors.capacidad_pasajeros ? 'error' : ''}
                min="1"
                max="50"
              />
              {errors.capacidad_pasajeros && (
                <span className="error-message">{errors.capacidad_pasajeros.message}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="tipo_vehiculo">Tipo de Vehículo</label>
              <select
                id="tipo_vehiculo"
                {...register('tipo_vehiculo')}
                className={errors.tipo_vehiculo ? 'error' : ''}
              >
                <option value="">Selecciona un tipo</option>
                <option value="Sedán">Sedán</option>
                <option value="SUV">SUV</option>
                <option value="Van">Van</option>
                <option value="Pickup">Pickup</option>
                <option value="Camioneta">Camioneta</option>
              </select>
              {errors.tipo_vehiculo && (
                <span className="error-message">{errors.tipo_vehiculo.message}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="color">Color</label>
              <input
                id="color"
                type="text"
                {...register('color')}
                className={errors.color ? 'error' : ''}
              />
              {errors.color && (
                <span className="error-message">{errors.color.message}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="ultima_fecha_mantenimiento">Última Fecha de Mantenimiento</label>
              <input
                id="ultima_fecha_mantenimiento"
                type="date"
                onClick={(e) => e.target.showPicker && e.target.showPicker()}
                {...register('ultima_fecha_mantenimiento')}
                className={errors.ultima_fecha_mantenimiento ? 'error' : ''}
              />
              {errors.ultima_fecha_mantenimiento && (
                <span className="error-message">{errors.ultima_fecha_mantenimiento.message}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="proximo_servicio">Próximo Servicio</label>
              <input
                id="proximo_servicio"
                type="date"
                onClick={(e) => e.target.showPicker && e.target.showPicker()}
                {...register('proximo_servicio')}
                className={errors.proximo_servicio ? 'error' : ''}
              />
              {errors.proximo_servicio && (
                <span className="error-message">{errors.proximo_servicio.message}</span>
              )}
            </div>

            <div className="form-group" style={{ alignItems: 'center' }}>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  {...register('equipaje')}
                />
                <span className="checkmark"></span>
                Equipaje
              </label>
            </div>

            <div className="form-group" style={{ alignItems: 'center' }}>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  {...register('seguro_vigente')}
                />
                <span className="checkmark"></span>
                Seguro Vigente
              </label>
            </div>

            <div className="form-group" style={{ alignItems: 'center', display: 'flex', justifyContent: 'center' }}>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  {...register('activo')}
                />
                <span className="checkmark"></span>
                Activo
              </label>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn--secondary" onClick={onClose}>
              Cancelar
            </button>
            <button 
              type="submit" 
              className="btn btn--primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Guardando...' : (vehicle ? 'Actualizar' : 'Crear')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VehicleForm;
